/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The NEa list and its sunset, as data instead of prose.
 *
 * The NEa's public "lijst van inboekdienstverleners" got its last update in
 * September 2026 and lapsed on 1 October 2026 (the list page said so itself;
 * docs/nea/README.md keeps the dated snapshots). Our /providers page reads
 * our own database, so no data flow broke; the page's source line says "the
 * NEa no longer publishes a list; this is our editorial register, based on
 * the final NEa version of <date>". Until 2026-10-14 the copy also carried the
 * phase before the sunset ("based on the NEa list, version <date>", with a
 * link to the list), chosen by the date; that phase is over for good, and its
 * copy and its branches are gone.
 *
 * Plain JS: read by the page, by /api/nea-providers, and by the prerender.
 */

/**
 * The NEa's OWN publication date of the list version our register was last
 * reconciled against — the "Publicatiedatum" on the document, not the day we
 * fetched it (the snapshot under docs/nea/ was taken on 2026-08-31 and shows
 * this 07-08-2026 version). The page says "versie van <date>" and links the
 * document, so the two must agree. Bump it when a newer NEa version is
 * archived under docs/nea/ — the final September 2026 version is the last
 * bump this constant will ever get.
 */
export const NEA_LIST_VERSION_DATE = '2026-08-07';

/** The day the NEa stopped publishing the list (Amsterdam midnight, not UTC). */
export const NEA_LIST_SUNSET_DATE = '2026-10-01';

/**
 * The list document itself (200 as of 2026-09-02). The previous source link
 * pointed at the NEa's ERE topic page, which now answers 404.
 */
export const NEA_LIST_URL =
  'https://www.emissieautoriteit.nl/documenten/2026/02/02/lijst-van-inboekdienstverleners';

/**
 * Always `frozen`: the list lapsed on NEA_LIST_SUNSET_DATE and no clock brings
 * it back. Until 2026-10-14 this compared the date (`live` before the
 * sunset); every payload since 1 October 2026 said `frozen`, and no page
 * renders anything else any more. The status block keeps the field, so the
 * API payload keeps its shape.
 *
 * @returns {'frozen'}
 */
export function neaListPhase() {
  return 'frozen';
}

/**
 * The status block the page renders from. Computed SERVER-side (API response
 * and prerender seed) so the baked HTML and the hydrating client agree on
 * every date in it.
 *
 * @param {{ editedAt?: string | null }} [opts]
 *   `editedAt` — ISO timestamp of the last edit to the provider records
 *   (the file's mtime), or null when unknown. Null renders as "no date",
 *   never as a made-up one.
 * @returns {{ phase: 'frozen', versionDate: string, editedAt: string|null, sourceUrl: string }}
 */
export function buildNeaListStatus(opts = {}) {
  const editedAt =
    typeof opts.editedAt === 'string' && !Number.isNaN(Date.parse(opts.editedAt))
      ? opts.editedAt
      : null;
  return {
    phase: neaListPhase(),
    versionDate: NEA_LIST_VERSION_DATE,
    editedAt,
    sourceUrl: NEA_LIST_URL,
  };
}
